import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
    Activity, 
    Database, 
    Shield, 
    Clock, 
    Mail,
    ArrowLeft,
    CheckCircle2,
    FileText,
    Trash2,
    Lock
} from 'lucide-react';

export default function DataDeletion() {

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-background to-blue-50 dark:from-slate-950/50 dark:via-background dark:to-blue-950/20">
            {/* Header */}
            <header className="border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Activity className="w-6 h-6 text-primary" />
                        <span className="font-heading font-bold text-lg">MediGuide AI</span>
                    </Link>
                    <Link to="/">
                        <Button variant="ghost" size="sm">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Home
                        </Button>
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 py-12">
                {/* Page Title */}
                <div className="text-center mb-10">
                    <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
                        <Database className="w-8 h-8 text-primary" />
                    </div>
                    <h1 className="text-3xl font-bold text-foreground mb-2">
                        Data Deletion Policy
                    </h1>
                    <p className="text-muted-foreground max-w-xl mx-auto">
                        Learn how to request deletion of your personal data from MediGuide
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                        Last updated: March 2025
                    </p>
                </div>

                {/* Quick Action Card */}
                <Card className="mb-8 border-primary/30 bg-primary/5">
                    <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <Trash2 className="w-8 h-8 text-primary" />
                                <div>
                                    <h3 className="font-semibold">Want to delete your data?</h3>
                                    <p className="text-sm text-muted-foreground">Use our account deletion page</p>
                                </div>
                            </div>
                            <Link to="/delete-account">
                                <Button data-testid="go-to-delete-btn">
                                    Delete My Data
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>

                {/* Your Data Rights */}
                <Card className="mb-8">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-primary" />
                            Your Data Rights
                        </CardTitle>
                        <CardDescription>
                            Under GDPR and other privacy regulations, you have the following rights
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {[
                                { 
                                    icon: FileText, 
                                    title: 'Right to Access', 
                                    desc: 'Request a copy of your personal data' 
                                },
                                { 
                                    icon: Trash2, 
                                    title: 'Right to Erasure', 
                                    desc: 'Request deletion of your personal data' 
                                },
                                { 
                                    icon: Lock, 
                                    title: 'Right to Restriction', 
                                    desc: 'Request limited processing of your data' 
                                },
                                { 
                                    icon: Database, 
                                    title: 'Right to Portability', 
                                    desc: 'Receive your data in a portable format' 
                                },
                            ].map((item, index) => (
                                <div key={index} className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                    <item.icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                                    <div>
                                        <h4 className="font-medium">{item.title}</h4>
                                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Data We Collect */}
                <Card className="mb-8">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Database className="w-5 h-5 text-primary" />
                            Data We Collect
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ul className="space-y-3">
                            {[
                                { text: 'Account information (name, email, password hash)', deletable: true },
                                { text: 'Profile details (phone, address, date of birth)', deletable: true },
                                { text: 'Health assessments and symptom checks', deletable: true },
                                { text: 'Diagnosis history and recommendations', deletable: true },
                                { text: 'Medical history and GP details', deletable: true },
                                { text: 'Health diary entries', deletable: true },
                                { text: 'Consultation requests and messages', deletable: true },
                                { text: 'Payment and subscription information', deletable: true },
                            ].map((item, index) => (
                                <li key={index} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                    <span>{item.text}</span>
                                    {item.deletable && (
                                        <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full">
                                            Can be deleted
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>

                {/* Deletion Process */}
                <Card className="mb-8">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-primary" />
                            Deletion Process
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-4">
                            {[
                                { 
                                    step: '1', 
                                    title: 'Submit Request', 
                                    desc: 'Submit your deletion request through our app or via email' 
                                },
                                { 
                                    step: '2', 
                                    title: 'Verification', 
                                    desc: 'We verify your identity to prevent unauthorized deletions' 
                                },
                                { 
                                    step: '3', 
                                    title: 'Processing', 
                                    desc: 'Your data is queued for deletion from all our systems' 
                                },
                                { 
                                    step: '4', 
                                    title: 'Confirmation', 
                                    desc: 'You receive confirmation once deletion is complete (within 30 days)' 
                                },
                            ].map((item, index) => (
                                <div key={index} className="flex items-start gap-4">
                                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                                        {item.step}
                                    </div>
                                    <div>
                                        <h4 className="font-medium">{item.title}</h4>
                                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* How to Request */}
                <Card className="mb-8">
                    <CardHeader>
                        <CardTitle>How to Request Data Deletion</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="grid sm:grid-cols-2 gap-6">
                            <div className="p-6 bg-primary/5 rounded-xl border border-primary/20">
                                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-primary" />
                                    Method 1: In-App
                                </h3>
                                <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                                    <li>Log in to your account</li>
                                    <li>Go to Settings</li>
                                    <li>Click "Delete Account"</li>
                                    <li>Confirm deletion</li>
                                </ol>
                                <Link to="/delete-account" className="mt-4 inline-block">
                                    <Button size="sm" data-testid="delete-in-app-btn">
                                        Delete via App
                                    </Button>
                                </Link>
                            </div>

                            <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-xl border">
                                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                                    <Mail className="w-5 h-5 text-primary" />
                                    Method 2: Email
                                </h3>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Send a deletion request to:
                                </p>
                                <a 
                                    href="mailto:support@mediguide.online?subject=Data%20Deletion%20Request&body=I%20would%20like%20to%20request%20deletion%20of%20all%20my%20personal%20data.%0A%0AEmail%20on%20account%3A%20%5BYour%20email%5D%0A%0AI%20understand%20this%20is%20permanent."
                                    className="text-primary font-medium hover:underline"
                                >
                                    support@mediguide.online
                                </a>
                                <p className="text-xs text-muted-foreground mt-3">
                                    Include your account email in the request
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Retention Notice */}
                <Card className="mb-8 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
                    <CardHeader>
                        <CardTitle className="text-amber-700 dark:text-amber-400">
                            Data Retention Notice
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        <p>
                            Some data may be retained for legal, security, or legitimate business purposes:
                        </p>
                        <ul className="space-y-2 list-disc list-inside text-muted-foreground">
                            <li>Transaction records (required for financial compliance)</li>
                            <li>Security logs (retained for 90 days)</li>
                            <li>Anonymized analytics data (cannot identify you)</li>
                        </ul>
                    </CardContent>
                </Card>

                {/* Contact */}
                <div className="text-center p-8 bg-slate-50 dark:bg-slate-800/50 rounded-2xl">
                    <h3 className="font-semibold text-lg mb-2">Need Help?</h3>
                    <p className="text-muted-foreground mb-4">
                        Contact our Data Protection team for any questions
                    </p>
                    <a href="mailto:support@mediguide.online">
                        <Button variant="outline" data-testid="contact-support-btn">
                            <Mail className="w-4 h-4 mr-2" />
                            support@mediguide.online
                        </Button>
                    </a>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t bg-card py-6 mt-12">
                <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <Activity className="w-5 h-5 text-primary" />
                        <span className="font-heading font-semibold">MediGuide AI</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-muted-foreground">
                        <Link to="/privacy" className="hover:text-primary transition-colors">
                            Privacy Policy
                        </Link>
                        <Link to="/terms" className="hover:text-primary transition-colors">
                            Terms of Service
                        </Link>
                        <Link to="/delete-account" className="hover:text-primary transition-colors">
                            Delete Account
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
